import { useState } from 'react';
import { getDealerResponse, mapItemToResponse } from '../data/dealerResponses';
import { useUnlock } from '../hooks/useUnlock';
import { UNLOCK_PRICE_RUPEES } from '../utils/unlock';
import PaywallModal from './PaywallModal';
import {
  MessageSquare,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Phone,
  Copy,
  Check,
  Lock,
} from 'lucide-react';

const DealerHelper = ({ item, onClose }) => {
  const [expandedSection, setExpandedSection] = useState('sayThis');
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const unlocked = useUnlock();

  const responseType = mapItemToResponse(item.id, item.title);
  const response = getDealerResponse(responseType);

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const sections = [
    {
      id: 'sayThis',
      title: 'What to Say',
      icon: MessageSquare,
      color: 'brand',
      content: response.sayThis,
    },
    {
      id: 'dontAccept',
      title: 'Don\'t Accept These Excuses',
      icon: XCircle,
      color: 'danger',
      content: response.dontAccept,
    },
    {
      id: 'acceptable',
      title: 'Acceptable Responses',
      icon: CheckCircle2,
      color: 'brand',
      content: response.acceptable,
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden animate-scale-in">
      {/* Header */}
      <div className="px-4 py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="w-5 h-5 text-brand-400" />
          <h3 className="font-semibold">Dealer Conversation Helper</h3>
        </div>
        <p className="text-sm text-slate-300">
          Issue: {response.issue}
        </p>
        {response.severity === 'critical' && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-danger-500/20 rounded-full">
            <AlertTriangle className="w-3 h-3 text-danger-400" />
            <span className="text-xs text-danger-300 font-medium">Critical Issue</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="divide-y divide-slate-100">
        {sections.map(section => {
          const isExpanded = expandedSection === section.id;
          const Icon = section.icon;
          
          return (
            <div key={section.id}>
              <button
                onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Icon className={`w-5 h-5 ${
                    section.color === 'brand' ? 'text-brand-500' : 'text-danger-500'
                  }`} />
                  <span className="font-medium text-slate-800">{section.title}</span>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              
              {isExpanded && (
                <div className="px-4 pb-4 animate-slide-down">
                  <div className="space-y-2">
                    {(unlocked ? section.content : section.id === 'sayThis' ? section.content.slice(0, 1) : []).map((text, index) => (
                      <div 
                        key={index}
                        className={`p-3 rounded-xl text-sm flex items-start justify-between gap-2 ${
                          section.id === 'sayThis' 
                            ? 'bg-brand-50 border border-brand-200'
                            : section.id === 'dontAccept'
                            ? 'bg-danger-50 border border-danger-200'
                            : 'bg-brand-50 border border-brand-200'
                        }`}
                      >
                        <p className={
                          section.id === 'sayThis' 
                            ? 'text-brand-800'
                            : section.id === 'dontAccept'
                            ? 'text-danger-800'
                            : 'text-brand-800'
                        }>
                          {text}
                        </p>
                        {section.id === 'sayThis' && (
                          <button
                            onClick={() => handleCopy(text, index)}
                            className="p-1.5 rounded-lg hover:bg-brand-100 transition-colors"
                          >
                            {copiedIndex === index ? (
                              <Check className="w-4 h-4 text-brand-600" />
                            ) : (
                              <Copy className="w-4 h-4 text-brand-500" />
                            )}
                          </button>
                        )}
                      </div>
                    ))}

                    {!unlocked && (
                      <button
                        onClick={() => setShowPaywall(true)}
                        className="w-full p-4 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 hover:border-brand-300 hover:bg-brand-50 transition-colors text-left flex items-start gap-3"
                      >
                        <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                          <Lock className="w-4 h-4 text-slate-500" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-700">
                            Unlock all dealer scripts - ₹{UNLOCK_PRICE_RUPEES}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Exact lines to say, excuses to reject, acceptable fixes
                            and escalation steps for every issue.
                          </p>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Escalation */}
      {unlocked && response.escalation && (
        <div className="px-4 py-4 bg-slate-50 border-t border-slate-100">
          <div className="flex items-start gap-2">
            <Phone className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-slate-600">Escalation Step:</p>
              <p className="text-sm text-slate-800 mt-0.5">{response.escalation}</p>
            </div>
          </div>
        </div>
      )}

      {/* Close Button */}
      <div className="px-4 py-3 border-t border-slate-100">
        <button
          onClick={onClose}
          className="w-full py-2.5 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 transition-colors"
        >
          Close
        </button>
      </div>

      {showPaywall && (
        <PaywallModal
          feature="dealer-scripts"
          onClose={() => setShowPaywall(false)}
          onUnlocked={() => setShowPaywall(false)}
        />
      )}
    </div>
  );
};

export default DealerHelper;
